import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["question", "questionText", "score", "skip", "status", "retry",
    "followUp", "followUpText", "comment", "commentSubmit", "commentSkip"]
  static values = { url: String }

  connect() {
    this.finished = false
    this.ratingSaved = false
    this.sending = false
    if (this.urlValue) this.show()
  }

  activate(event) {
    this.urlValue = event.detail.url || ""
    if (event.detail.question) this.questionTextTarget.textContent = event.detail.question
    if (this.urlValue) this.show()
  }

  show() {
    this.element.hidden = false
    this.request({ decision: "shown" }).then(data => this.finish(data)).catch(() => {})
  }

  select(event) {
    if (this.finished || this.sending || this.ratingSaved) return
    this.answer = { decision: "score", score: Number(event.currentTarget.dataset.score) }
    this.scoreTargets.forEach(button => {
      const selected = button === event.currentTarget
      button.setAttribute("aria-pressed", String(selected))
      button.classList.toggle("bg-white", !selected)
      button.classList.toggle("bg-brand-50", selected)
      button.classList.toggle("border-brand-500", selected)
    })
    return this.send()
  }

  skip() {
    if (this.finished || this.sending || this.ratingSaved) return
    this.answer = { decision: "skip" }
    return this.send()
  }

  submitComment() {
    if (this.finished || this.sending || !this.ratingSaved) return
    const comment = this.commentTarget.value.trim()
    if (!comment || comment.length > 2000) {
      this.statusTarget.textContent = "Enter a suggestion of up to 2000 characters, or skip."
      this.commentTarget.focus()
      return
    }
    this.answer = { decision: "comment", comment }
    return this.send()
  }

  skipComment() {
    if (this.finished || this.sending || !this.ratingSaved) return
    this.answer = { decision: "skip_comment" }
    return this.send()
  }

  retry() {
    if (this.answer) return this.send()
  }

  async send() {
    if (this.finished || this.sending) return
    this.sending = true
    this.scoreTargets.forEach(button => { button.disabled = true })
    this.skipTarget.disabled = true
    this.commentSubmitTarget.disabled = true
    this.commentSkipTarget.disabled = true
    this.retryTarget.hidden = true
    this.statusTarget.textContent = "Saving your feedback…"
    try {
      this.finish(await this.request(this.answer))
    } catch {
      this.statusTarget.textContent = this.ratingSaved
        ? "Your score is saved. We couldn't save your suggestion. Retry or skip."
        : "Your conversation is saved. We couldn't save your feedback."
      this.retryTarget.hidden = false
      this.skipTarget.disabled = false
    } finally {
      this.sending = false
      this.commentSubmitTarget.disabled = false
      this.commentSkipTarget.disabled = false
    }
  }

  async request(answer) {
    const response = await fetch(this.urlValue, {
      method: "POST", credentials: "same-origin", cache: "no-store", keepalive: true,
      headers: { "Content-Type": "application/json", Accept: "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
      body: JSON.stringify(answer)
    })
    if (!response.ok) throw new Error("Feedback could not be saved")
    return response.json()
  }

  finish(data, broadcast = true) {
    if (this.finished) return
    if (data.question) this.questionTextTarget.textContent = data.question
    if (!["submitted", "skipped"].includes(data.status)) return
    this.ratingSaved = data.status === "submitted"
    this.questionTarget.hidden = true
    this.retryTarget.hidden = true
    if (data.follow_up?.status === "pending") {
      const newlyShown = this.followUpTarget.hidden
      this.followUpTextTarget.textContent = data.follow_up.prompt
      this.followUpTarget.hidden = false
      this.statusTarget.textContent = "Your score is saved. You can send a suggestion or skip."
      if (newlyShown) this.commentTarget.focus()
    } else {
      this.finished = true
      this.followUpTarget.hidden = true
      this.statusTarget.textContent = data.status === "submitted"
        ? "Feedback received. Thank you for helping us improve."
        : "You can leave this page."
    }
    if (broadcast) window.dispatchEvent(new CustomEvent("conversation-feedback:resolved", { detail: { url: this.urlValue, result: data } }))
  }

  resolved(event) {
    if (event.detail.url === this.urlValue) this.finish(event.detail.result, false)
  }

  modalClosed(event) {
    if (event.target.contains(this.element)) this.leave()
  }

  leave() {
    if (!this.urlValue || this.finished || this.sending) return
    // Do not send an unsent draft. If a suggestion was already submitted, retry
    // that exact request; otherwise Skip the optional follow-up and keep the score.
    const answer = this.ratingSaved
      ? (["comment", "skip_comment"].includes(this.answer?.decision) ? this.answer : { decision: "skip_comment" })
      : (this.answer || { decision: "skip" })
    this.request(answer).catch(() => {})
  }
}
